import { Container } from '@/components';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Building2,
  CreditCard,
  Loader2,
  Receipt,
  Wallet,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import companiesService from '@/services/companies/companiesService';
import { useAuthContext } from '@/auth';
import { toAbsoluteUrl } from '@/utils';
import { SubscriptionUsers } from './SubscriptionUsers';
import { SeatsCard } from './SeatsCard';

const formatCurrency = (value, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const getInclusiveAmount = (baseAmount, feeAmount, totalAmount) => {
  const parsedTotal = Number(totalAmount || 0);
  if (parsedTotal > 0) {
    return parsedTotal;
  }

  return Number(baseAmount || 0) + Number(feeAmount || 0);
};

const formatDate = (value) => {
  if (!value) return 'N/A';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const getPercent = (part, total) => {
  if (!total) return 0;
  return Math.min(100, Math.round((part / total) * 100));
};

const statusTheme = {
  active: {
    badge: 'badge-success',
    label: 'Active',
  },
  inactive: {
    badge: 'badge-warning',
    label: 'Inactive',
  },
  expired: {
    badge: 'badge-secondary',
    label: 'Expired',
  },
  ended: {
    badge: 'badge-warning',
    label: 'Inactive',
  },
  cancelled: {
    badge: 'badge-warning',
    label: 'Inactive',
  },
  canceled: {
    badge: 'badge-warning',
    label: 'Inactive',
  },
};

const getStatusBadge = (value) => {
  const normalizedValue = value?.toLowerCase?.() || 'inactive';
  const theme = statusTheme[normalizedValue] || statusTheme.inactive;

  return (
    <span className={`badge badge-pill badge-outline ${theme.badge} gap-1 items-center`}>
      <span className={`badge badge-dot size-1.5 ${theme.badge}`}></span>
      {theme.label}
    </span>
  );
};

const OverviewCard = ({ icon: Icon, label, value, meta, iconClassName, iconWrapperClassName }) => (
  <div className="card">
    <div className="card-body p-5">
      <div className={`flex size-9 items-center justify-center rounded-xl mb-2 ${iconWrapperClassName}`}>
        <Icon className={`size-4 ${iconClassName}`} />
      </div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase text-gray-600">{label}</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
          {meta ? <div className="mt-2 text-sm text-gray-600">{meta}</div> : null}
        </div>
      </div>
    </div>
  </div>
);

const LoadingCard = () => (
  <div className="card">
    <div className="card-body p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="w-full space-y-3">
          <Skeleton className="h-4 w-28 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-md" />
          <Skeleton className="h-4 w-44 rounded-md" />
        </div>
        <Skeleton className="h-10 w-10 rounded-md" />
      </div>
    </div>
  </div>
);

const LoadingTable = () => (
  <div className="card">
    <div className="card-header">
      <div className="space-y-2">
        <Skeleton className="h-5 w-40 rounded-md" />
        <Skeleton className="h-4 w-64 rounded-md" />
      </div>
    </div>
    <div className="card-body p-0">
      <div className="overflow-x-auto">
        <table className="table table-auto">
          <thead>
            <tr>
              {Array.from({ length: 5 }).map((_, index) => (
                <th key={index}>
                  <Skeleton className="h-4 w-20 rounded-md" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: 5 }).map((__, cellIndex) => (
                  <td key={cellIndex}>
                    <Skeleton className="h-10 w-10 rounded-md" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const LoadingPaymentMethodCard = () => (
  <div className="card overflow-hidden border border-gray-200 shadow-sm">
    <div className="card-body p-0">
      <div className="grid gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="border-b border-light bg-light-active p-6 dark:bg-coal-100 lg:border-b-0 lg:border-e">
          <div className="flex flex-col h-full">
            <Skeleton className="h-[150px] w-full rounded-2xl" />
            <Skeleton className="mt-6 h-7 w-48 rounded-md" />
            <Skeleton className="mt-3 h-4 w-full rounded-md" />
            <Skeleton className="mt-2 h-4 w-5/6 rounded-md" />
          </div>
        </div>

        <div className="p-6 lg:p-7">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_240px]">
            <div className="card p-5 shadow-sm dark:bg-coal-100">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="w-full space-y-2">
                  <Skeleton className="h-5 w-36 rounded-md" />
                  <Skeleton className="h-4 w-4/5 rounded-md" />
                </div>
              </div>
              <Skeleton className="h-14 w-full rounded-2xl" />
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <Skeleton className="h-4 w-44 rounded-md" />
                <Skeleton className="h-10 w-[170px] rounded-md" />
              </div>
            </div>

            <div className="bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300">
              <Skeleton className="h-4 w-32 rounded-md" />
              <div className="mt-4 space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Skeleton className="h-4 w-32 rounded-md" />
                    <Skeleton className="h-3 w-full rounded-md" />
                    <Skeleton className="h-3 w-5/6 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AnimatedUsageBar = ({ label, value, total, colorClass }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-gray-700">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
    <div className="h-2.5 overflow-hidden rounded-full bg-gray-200">
      <div
        className={`h-2.5 rounded-full transition-all duration-700 ease-out ${colorClass}`}
        style={{ width: `${getPercent(value, total)}%` }}
      />
    </div>
  </div>
);

const CARD_BRAND_LABELS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

const ManagePaymentMethodCard = ({ paymentMethod }) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const brandLabel = paymentMethod?.brand
    ? CARD_BRAND_LABELS[paymentMethod.brand] || paymentMethod.brand
    : null;

  const expiry = paymentMethod?.exp_month && paymentMethod?.exp_year
    ? `${String(paymentMethod.exp_month).padStart(2, '0')}/${String(paymentMethod.exp_year).slice(-2)}`
    : null;

  const openBillingPortal = async () => {
    setIsRedirecting(true);

    try {
      const { status, data } = await companiesService.createBillingPortalSession();

      if (status && data?.url) {
        // Full redirect rather than a popup — popups get blocked, and Stripe
        // sends the user back to `return_url` when they are done.
        window.location.href = data.url;
        return;
      }

      toast.error('Could not open the billing portal.', {
        description: 'Please try again in a moment.',
      });
      setIsRedirecting(false);
    } catch (error) {
      toast.error('Could not open the billing portal.', {
        description: error?.message || 'Please try again later.',
      });
      setIsRedirecting(false);
    }
  };

  return (
    <div className="card overflow-hidden border border-gray-200 shadow-sm">
      <div className="card-body p-0">
        <div className="grid gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div
            className="relative overflow-hidden border-b border-light bg-light-active p-6 dark:bg-coal-100 lg:border-b-0 lg:border-e"
            style={{
              backgroundImage: `url('${toAbsoluteUrl('/media/images/2600x1200/bg-4.png')}')`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'left -170px bottom -40px',
              backgroundSize: '620px',
            }}
          >
            <div className="relative z-[1] flex h-full flex-col">
              <h3 className="mt-2 text-xl font-semibold text-gray-900">Payment Method</h3>
              <p className="mt-3 text-sm leading-6 text-gray-700">
                Manage the card your company uses for upcoming payments. Card changes happen on Stripe&apos;s own secure billing page.
              </p>
            </div>
          </div>

          <div className="p-6 lg:p-7">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_240px]">
              <div className="card bg-light-active p-5 shadow-sm dark:bg-coal-100">
                <div className="text-sm font-medium text-gray-900">Card On File</div>

                {paymentMethod ? (
                  <div className="mt-4 flex items-center gap-4 rounded-xl border-2 border-gray-300 bg-white px-4 py-3.5 shadow-sm dark:border-gray-600 dark:bg-[--tw-navbar-bg-dark]">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
                      <CreditCard className="size-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {brandLabel ? `${brandLabel} ` : ''}•••• {paymentMethod.last_four}
                      </div>
                      {expiry && (
                        <div className="mt-0.5 text-xs text-gray-600">Expires {expiry}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center gap-3 rounded-xl border-2 border-dashed border-gray-300 bg-white px-4 py-3.5 dark:border-gray-600 dark:bg-[--tw-navbar-bg-dark]">
                    <AlertCircle className="size-5 shrink-0 text-warning" />
                    <div className="text-sm text-gray-700">
                      No card on file yet. Add one to keep your subscription active.
                    </div>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs leading-5 text-gray-500">
                    You will be taken to Stripe and returned here when you are done.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary min-w-[190px] justify-center"
                    onClick={openBillingPortal}
                    disabled={isRedirecting}
                  >
                    {isRedirecting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Opening Stripe...
                      </>
                    ) : (
                      <>
                        <CreditCard className="size-4" />
                        {paymentMethod ? 'Manage Payment Method' : 'Add Payment Method'}
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300">
                <div className="text-xs uppercase text-gray-600">What You Can Do There</div>
                <div className="mt-4 space-y-4">
                  <div className="flex gap-3 flex-col">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Update or replace your card</div>
                      <div className="mt-1 text-xs leading-5 text-gray-600">Add a new card and make it the default for future billing.</div>
                    </div>
                  </div>
                  <div className="flex gap-3 flex-col">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Download invoices and receipts</div>
                      <div className="mt-1 text-xs leading-5 text-gray-600">Your full Stripe billing history is available in one place.</div>
                    </div>
                  </div>
                  <div className="flex gap-3 flex-col">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Card details never touch our servers</div>
                      <div className="mt-1 text-xs leading-5 text-gray-600">Stripe collects and stores them directly, so we never see the number.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ManagePaymentMethodCard.propTypes = {
  paymentMethod: PropTypes.shape({
    brand: PropTypes.string,
    last_four: PropTypes.string,
    exp_month: PropTypes.number,
    exp_year: PropTypes.number,
  }),
};

const PlanUsage = () => {
  const { auth } = useAuthContext();
  const companyId = auth?.user?.company_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const fetchBillingSummary = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, status } = await companiesService.getCompanyBillingSummary(companyId);
        if (status) {
          setDashboard(data || null);
        }
      } catch (error) {
        toast.error('Failed to fetch billing dashboard.', {
          description: error?.message || 'Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBillingSummary();
  }, [companyId, reloadKey]);

  // Coming back from Stripe. The webhook records the payment; this just nudges
  // the page so the firm sees the result without a manual refresh.
  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');

    if (!checkoutResult) {
      return;
    }

    if (checkoutResult !== 'success' || !sessionId) {
      toast.info('Checkout cancelled', { description: 'No payment was taken.' });
      setSearchParams({}, { replace: true });
      return;
    }

    const confirmCheckout = async () => {
      try {
        const { status, data } = await companiesService.confirmSubscriptionCheckout(sessionId);

        if (status && data?.paid) {
          toast.success('Payment received!', { description: 'Your subscription is up to date.' });
        } else {
          toast.info('Your payment is still processing', {
            description: 'This page will catch up as soon as Stripe confirms it.',
          });
        }
      } catch (error) {
        toast.error('Could not confirm your payment', {
          description: error?.message || 'If you were charged, refresh this page in a moment.',
        });
      } finally {
        setSearchParams({}, { replace: true });
        setReloadKey((key) => key + 1);
      }
    };

    confirmCheckout();
  }, [searchParams, setSearchParams]);

  const summary = useMemo(() => {
    const subscriptions = dashboard?.subscriptions || [];
    const currency = subscriptions[0]?.currency || 'USD';
    const totalAmount = subscriptions.reduce(
      (sum, item) => sum + getInclusiveAmount(item?.base_amount, item?.stripe_fee, item?.total_amount),
      0
    );
    const activeSubscriptions = subscriptions.filter((item) => item?.status?.toLowerCase() === 'active').length;
    const inactiveSubscriptions = subscriptions.filter(
      (item) => item?.ended_at || ['expired', 'cancelled', 'deleted', 'inactive'].includes(item?.status?.toLowerCase?.())
    ).length;
    const nextExpiry = subscriptions
      .map((item) => item?.expires_at)
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b))[0] || null;

    return {
      subscriptions,
      currency,
      totalAmount,
      activeSubscriptions,
      inactiveSubscriptions,
      nextExpiry,
      credit: Number(dashboard?.credit || 0),
    };
  }, [dashboard]);

  if (loading) {
    return (
      <Container>
        <div className="space-y-6 pb-6">
          <div className="grid gap-3 rounded-xl sm:grid-cols-3 xl:min-w-[520px]">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className='bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300'>
                <Skeleton className="h-4 w-24 rounded-md" />
                <Skeleton className="mt-3 h-5 w-32 rounded-md" />
                <Skeleton className="mt-2 h-4 w-40 rounded-md" />
              </div>
            ))}
          </div>

          <LoadingPaymentMethodCard />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>

          <LoadingTable />
        </div>
      </Container>
    );
  }

  if (!dashboard) {
    return (
      <Container>
        <div className="card">
          <div className="card-body p-6 text-sm text-gray-600">No billing dashboard data available.</div>
        </div>
      </Container>
    );
  }

  const subscriptionUtilization = getPercent(summary.activeSubscriptions, summary.subscriptions.length);

  return (
    <Container>
      <div className="space-y-6 pb-6">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="grid gap-3 rounded-xl sm:grid-cols-3 xl:min-w-[520px]">
            <div className='bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300'>
              <div className="text-xs uppercase tracking-[0.16em] text-gray-500">Company</div>
              <div className="mt-2 text-sm font-semibold text-gray-900">{dashboard.company_name || 'N/A'}</div>
              <div className="text-sm text-gray-600">Billing dashboard overview</div>
            </div>
            <div className='bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300'>
              <div className="text-xs uppercase tracking-[0.16em] text-gray-500">Next Expiry</div>
              <div className="mt-2 text-sm font-semibold text-gray-900">{formatDate(summary.nextExpiry)}</div>
              <div className="text-sm text-gray-600">{summary.subscriptions.length} subscription records</div>
            </div>
            <div className='bg-light-active dark:bg-coal-100 p-4 rounded-xl border border-dashed border-gray-300'>
              <div className="text-xs uppercase tracking-[0.16em] text-gray-500">Utilization</div>
              <div className="mt-2 text-sm font-semibold text-gray-900">
                {summary.activeSubscriptions} active of {summary.subscriptions.length}
              </div>
              <div className="text-sm text-gray-600">{subscriptionUtilization}% currently active</div>
            </div>
          </div>
        </div>

        <SeatsCard key={reloadKey} />

        {companyId ? <ManagePaymentMethodCard paymentMethod={dashboard.payment_method} /> : null}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
          <OverviewCard
            icon={Receipt}
            label="Total Amount Paid"
            value={formatCurrency(summary.totalAmount, summary.currency)}
            meta={`${summary.subscriptions.length} subscriptions in ${summary.currency}`}
            iconWrapperClassName="bg-primary-light"
            iconClassName="text-primary"
          />
          <OverviewCard
            icon={Wallet}
            label="Available Credit"
            value={formatCurrency(summary.credit, summary.currency)}
            meta="Credit available against future charges"
            iconWrapperClassName="bg-danger-light"
            iconClassName="text-danger"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_380px]">
          <div className="card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Subscription Overview</h3>
              </div>
            </div>
            <div className="card-body p-6">
              <div className="space-y-5">
                <AnimatedUsageBar
                  label="Active subscriptions"
                  value={summary.activeSubscriptions}
                  total={summary.subscriptions.length}
                  colorClass="bg-success-active"
                />

                <AnimatedUsageBar
                  label="Inactive subscriptions"
                  value={summary.inactiveSubscriptions}
                  total={summary.subscriptions.length}
                  colorClass="bg-danger-active"
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl">
                  <Building2 className="size-5" />
                </div>
                <div>
                  <h3 className="card-title">Billing Notes</h3>
                </div>
              </div>
            </div>
            <div className="card-body p-6">
              <div className="divide-y-2 text-sm">
                <div className="flex items-start justify-between py-2 gap-3">
                  <span className="text-gray-600">Company</span>
                  <span className="font-medium text-gray-900">{dashboard.company_name || 'N/A'}</span>
                </div>
                <div className="flex items-start justify-between py-2 gap-3">
                  <span className="text-gray-600">Next expiry</span>
                  <span className="font-medium text-gray-900">{formatDate(summary.nextExpiry)}</span>
                </div>
                <div className="flex items-start justify-between py-2 gap-3">
                  <span className="text-gray-600">Credit</span>
                  <span className="font-medium text-gray-900">{formatCurrency(summary.credit, summary.currency)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SubscriptionUsers />
      </div>
    </Container>
  );
};

export { PlanUsage };
